import type React from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { TypeButton } from '../../../../utils/button';
import { TypeCheckbox } from '../../../../utils/checkbox';
import {
	type IFieldReadinessSchema,
	type ILocationDetailsSchema,
	type IPersonalDetailsSchema,
	type ISummarySchema,
	type tabTitle,
} from '../helper';
import { CTASection, SummaryContainer, TermsContainer, TermsText } from '../styled';
import SummaryLocation from './summary.location';
import SummaryPersonalInfo from './summary.personalinfo';
import SummaryReadinessInfo from './summary.readinessinfo';

interface BIC {
	prevPage: () => void;
	onSubmit: () => void;
	onEdit: (titleArg: tabTitle) => () => void;
	personalDetailsHookForm: UseFormReturn<IPersonalDetailsSchema>;
	locationDetailsHookForm: UseFormReturn<ILocationDetailsSchema>;
	fieldReadinessDetailsHookForm: UseFormReturn<IFieldReadinessSchema>;
	summaryHookForm: UseFormReturn<ISummarySchema>;
	isLoading: boolean;
	contentRef: React.RefObject<HTMLDivElement | null>;
}

const VerificationBanner = ({ hookForm }: { hookForm: UseFormReturn<IPersonalDetailsSchema> }) => {
	const faceId = hookForm.watch('faceId');
	const cardImage = hookForm.watch('votersCardImage');
	const reviewRequired = hookForm.watch('kycReviewRequired');
	const reasons = hookForm.watch('kycReviewReasons') ?? [];

	const tone: 'success' | 'warning' | 'idle' = reviewRequired ? 'warning' : faceId ? 'success' : 'idle';

	const heading =
		tone === 'success' ? 'Verification passed' : tone === 'warning' ? 'Submitting for manual review' : 'Verification incomplete';

	const description =
		tone === 'success'
			? 'Your selfie was confirmed live and your voter card details look good.'
			: tone === 'warning'
				? `An admin will manually review your application before approval. Reason${
						reasons.length > 1 ? 's' : ''
					}: ${reasons.join(', ')}.`
				: 'You haven’t completed identity verification yet. Go back to step 1 to add your selfie.';

	const bg =
		tone === 'success' ? 'var(--color-success-bg)' : tone === 'warning' ? 'var(--color-warning-bg)' : 'var(--color-bg-tertiary)';
	const fg =
		tone === 'success'
			? 'var(--color-success-text)'
			: tone === 'warning'
				? 'var(--color-warning-text)'
				: 'var(--color-text-secondary)';
	const icon = tone === 'success' ? '✓' : tone === 'warning' ? '⚠' : 'ℹ';

	return (
		<div
			style={{
				display: 'flex',
				gap: '12px',
				padding: '14px 16px',
				borderRadius: '12px',
				backgroundColor: bg,
				color: fg,
				alignItems: 'flex-start',
				marginBottom: '8px',
			}}
		>
			<span
				aria-hidden
				style={{ fontSize: '18px', lineHeight: 1, marginTop: 2 }}
			>
				{icon}
			</span>
			<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
				<strong
					style={{
						fontFamily: 'var(--font-family-semibold)',
						fontSize: '13px',
						letterSpacing: 'var(--tracking-wide)',
						textTransform: 'uppercase',
					}}
				>
					{heading}
				</strong>
				<span
					style={{
						fontFamily: 'var(--font-family-regular)',
						fontSize: 'var(--text-body-sm-size)',
						lineHeight: 1.5,
					}}
				>
					{description}
				</span>
				{cardImage && (
					<span
						style={{
							fontFamily: 'var(--font-family-medium)',
							fontSize: '12px',
							opacity: 0.85,
						}}
					>
						Voter card image attached.
					</span>
				)}
			</div>
		</div>
	);
};

const Summary: React.FC<BIC> = ({
	prevPage,
	onSubmit,
	onEdit,
	personalDetailsHookForm,
	locationDetailsHookForm,
	fieldReadinessDetailsHookForm,
	summaryHookForm,
	isLoading,
	contentRef,
}) => (
	<>
		<VerificationBanner hookForm={personalDetailsHookForm} />

		<SummaryContainer ref={contentRef}>
			<SummaryPersonalInfo
				hookForm={personalDetailsHookForm}
				onEdit={onEdit('Basic Information')}
			/>
			<SummaryLocation
				hookForm={locationDetailsHookForm}
				onEdit={onEdit('Location Information')}
				resultType={fieldReadinessDetailsHookForm.watch('resultType')}
			/>
			<SummaryReadinessInfo
				hookForm={fieldReadinessDetailsHookForm}
				onEdit={onEdit('Field Readiness')}
			/>
		</SummaryContainer>

		<TermsContainer>
			<TypeCheckbox {...summaryHookForm.register('tc')} />
			<TermsText>I have reviewed all the information above and confirm that it is accurate and complete.</TermsText>
		</TermsContainer>

		<CTASection>
			<TypeButton
				title='← Previous'
				onClick={prevPage}
				className='prevbtn'
			/>
			<TypeButton
				title='Submit application →'
				onClick={onSubmit}
				btntype='primary'
				disabled={!summaryHookForm.watch().tc}
				load={isLoading}
				className='nextbtn'
			/>
		</CTASection>
	</>
);

export default Summary;
